import { IMeeting } from "@mctypes/meeting.types";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Badge } from "reactstrap";
import {
  Avatar,
  Checkbox,
  CheckIcon,
  ConfirmDialog,
  CreateNewButton,
  FailedAlert,
  IconToggle,
  TaskCompletedCount,
} from "../../../../../components";
import { CellEditFormatter, expandColumnCaret, expandHeaderColumn } from "../../../../../components/columnGlobal";
import { getIssuesCompletedCount, getProjectIdFromUrl, sortArrayByNumber } from "../../../../utils";
import { ISSUE_DATAFILEDS_ENUM } from "../../../issuesApp/issueConstants";

import { Box, Button, CloseButton, Group, TextInput, Tooltip } from "@mantine/core";
import { IIssueTodoItem, IUserPartial } from "@schemas/issueSchema";
import { IconPrinter, IconSend, IconSortAscendingNumbers, IconUserCheck } from "@tabler/icons-react";
import { ColDef, GridApi, IRowNode, SideBarDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { useHistory } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { useAppSelector } from "../../../../../hooks";
import { useAgGridReduxState } from "../../../../../hooks/useAgGridReduxState";
import { STANDARD_COLORS } from "../../../../globalConstants";
import {
  useCreateMeetingAgendaItemMutation,
  useUpdateAgendaOrderMutation,
  useUpdateMeetingAgendaMutation,
} from "../../meetingAgendaRTK";
import { useUpdateAllAgendaIdsMutation } from "../../meetingRTK";
import AgendaItem from "./AgendaItem";
import SendMeetingAgendaByMailModal from "./SendMeetingAgendaByMailModal";

const AgendaUserCellRenderer: React.FC<{ user?: IUserPartial | null; avatarSize?: string | number }> = ({
  user,
  avatarSize = "md",
}) => {
  return user != null ? (
    <div className="d-flex align-items-center">
      <Avatar id={user._id} name={user.name} size={avatarSize} />
      <div className="ml-2">{user.name}</div>
    </div>
  ) : (
    <div></div>
  );
};

const Agenda: React.FC<{ singleMeeting: IMeeting.MeetingPopulated }> = ({ singleMeeting }) => {
  const [sendAgendaMailModalOpen, setSendAgendaMailModalOpen] = React.useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>("");
  const [userFilterActive, setUserFilterActive] = useState<boolean>(false);
  const history = useHistory();
  const user = useAppSelector((state) => state.globalReducer.user);
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const agendaItems = sortArrayByNumber((singleMeeting?.agendaItems as IMeeting.AgendaItem[]) || [], "order");
  const meetingParticipantUsers = useMemo(
    (): IUserPartial[] =>
      ((singleMeeting?.participants ?? []) as IMeeting.ParticipantBase[])
        .map((participant) => participant.user)
        .filter(
          (participant): participant is Exclude<IMeeting.ParticipantBase["user"], string> =>
            participant != null && typeof participant !== "string",
        )
        .map((participant) => ({ _id: participant._id, name: participant.name })),
    [singleMeeting?.participants],
  );
  let refContainer = useRef(null);
  const [updateAgendaItemOrder] = useUpdateAgendaOrderMutation();
  const gridRef = useRef<AgGridReact>(null);

  const setPrinterFriendly = (api: GridApi) => {
    api.sizeColumnsToFit();
    api.setGridOption("domLayout", "print");
  };

  const setNormal = (api: GridApi) => {
    api.setGridOption("domLayout", undefined);
  };

  const TodoList: React.FC<{ todos: IIssueTodoItem[] }> = ({ todos }) => {
    const [showAll, setShowAll] = useState(false);
    const handleShowAll = (e) => {
      setShowAll(!showAll);
      e.preventDefault();
    };
    return (
      <div className="m-2">
        {todos.map((todo, index) => (
          <p key={todo._id as string} className={`pb-1 m-0 ${index > 4 && !showAll ? "d-none" : ""}`}>
            {todo.completedDate != null && <i className="fa fa-check-circle fa-fw success-text" />}
            {todo.completedDate == null && <i className="fa fa-circle fa-fw gray-text" />}
            {todo.text}
          </p>
        ))}
        {todos.length > 4 && (
          <Button
            size="sm"
            style={{ fontSize: ".8em", height: "auto", padding: "6px 10px" }}
            onClick={(e) => handleShowAll(e)}
          >
            {showAll ? "Vis mindre" : "Vis alle"}
            <i className={`fa fa-angle-down ml-1 ${showAll && "rotateIcon"}`}></i>
          </Button>
        )}
      </div>
    );
  };
  const onRowDragEnd = (event) => {
    const draggedRow = event.node.data;
    const newPosition = event.node.rowIndex + 1;
    if (draggedRow.order != newPosition) {
      updateAgendaItemOrder({
        projectId: projectId,
        agendaId: draggedRow._id,
        order: newPosition,
      });
    }
  };
  const doesExternalFilterPass = useCallback((node: IRowNode<any>): boolean => {
    const hasConnectedIssueWithUser = (node.data?.connectedIssues || []).some(
      (issue) => issue.assignedTo?._id === user._id,
    );
    return (
      node.data.assignedTo?._id === user._id ||
      node.data.itemParticipants?.some((p) => p?._id === user._id) ||
      hasConnectedIssueWithUser
    );
  }, []);
  const getRowId = useCallback((params) => params.data._id, []);
  const baseColumns: ColDef<IMeeting.AgendaItem>[] = [
    {
      headerName: "",
      colId: "expandColumn",
      cellStyle: { padding: "0px" },
      cellRenderer: (props) => {
        const { node } = props;
        return (
          <div className={`expandIcon${node.expanded === true ? " rotateIcon" : ""}`} data-expand-trigger="true">
            <i className="fa fa-angle-down"></i>
          </div>
        );
      },
      minWidth: 44,
      maxWidth: 44,
      suppressSizeToFit: true,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
    },
    {
      headerName: "IR",
      field: "notRelevant",
      width: 62,
      maxWidth: 62,
      minWidth: 62,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div id={"a" + row._id} className="d-flex w-100 align-items-center justify-content-center">
            <Checkbox
              mode="cross"
              isChecked={row?.notRelevant === true}
              setChecked={(e) => {
                e.preventDefault();
                updateAgendaItem({
                  projectId: projectId,
                  agendaId: row._id,
                  attribute: "notRelevant",
                  value: !row?.notRelevant,
                });
              }}
              disabled={false}
              color="gray"
            />
          </div>
        );
      },
    },
    {
      headerName: "Id",
      field: "id",
      rowDrag: true,

      cellRenderer: (params) => (
        <div data-expand-trigger="true" className="d-flex align-items-center">
          <Button
            miw={"fit-content"}
            color="mc-primary"
            variant="subtle"
            px={"xs"}
            size="sm"
            onClick={() => expandRow.renderer(params.data)}
          >
            <b>
              <u>{params.data.id}</u>
            </b>
          </Button>

          <TaskCompletedCount taskCompleteCount={getIssuesCompletedCount(params.data.connectedIssues)} />
        </div>
      ),
      minWidth: 115,
      cellClass: "p-0",
      suppressSizeToFit: true,
    },
    {
      headerName: "Starttid",
      field: "startTimeCalculated",
      width: 100,
      minWidth: 70,
      maxWidth: 110,
    },
    {
      headerName: "Tittel",
      field: "title",
      editable: true,
      minWidth: 150,
      cellRenderer: (params) => (
        <div className="d-flex align-items-center truncateHeaderText">
          <b className="two-line-text-truncate">{params.value}</b>
        </div>
      ),
      cellEditor: "agTextCellEditor",
    },
    {
      headerName: "Beskrivelse",
      field: "description",
      editable: true,
      minWidth: 150,
      maxWidth: 500,

      cellRenderer: (params) => {
        return CellEditFormatter(params.value, "description");
      },
      cellEditor: "agTextCellEditor",
    },
    {
      headerName: "Ansvarlig",
      field: ISSUE_DATAFILEDS_ENUM.ASSIGNEDTO_NAME,
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      suppressPaste: true,
      minWidth: 180,
      valueGetter: (params) => params.data.assignedTo?.name ?? null,
      valueFormatter: (params) => params.data.assignedTo?.name ?? "",
      cellEditorParams: {
        values: [null, ...meetingParticipantUsers],
        formatValue: (value: IUserPartial | null) => value?.name ?? "",
        allowTyping: true,
        filterList: true,
        cellRenderer: (params) => <AgendaUserCellRenderer user={params.value} avatarSize="15px" />,
      },
      context: {
        updateAttribute: "assignedTo",
      },
      cellRenderer: (params) => {
        return <AgendaUserCellRenderer user={params.data.assignedTo} />;
      },
    },
    {
      headerName: "Deltakere",
      field: "itemParticipants",
      cellRenderer: (params) => {
        const participants = params.data.itemParticipants || [];
        if (participants.length === 0) return null;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {participants.map((participant) => (
              <Tooltip key={participant._id} label={participant.name || "Ukjent deltaker"} withArrow openDelay={300}>
                <div>
                  <Avatar id={participant._id} name={participant.name} />
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    { headerName: "Formål", field: "purpose", editable: true, cellEditor: "agTextCellEditor" },
    {
      headerName: "Konklusjon",
      field: "conclusion",
      editable: true,
      minWidth: 150,
      maxWidth: 500,
      cellRenderer: (params) => {
        return CellEditFormatter(params.value, "conclusion");
      },
      cellEditor: "agTextCellEditor",
    },
    {
      headerName: "Forberedelser",
      field: "todos",
      suppressHeaderMenuButton: true,
      autoHeight: true,
      width: 20,
      maxWidth: 120,
      cellStyle: { alignItems: "center" },
      valueGetter: (params) => {
        return (params.data.todos || []).map((todo) => todo.text);
      },
      cellRenderer: (params) => {
        const todos = params.data.todos as IIssueTodoItem[];
        const completedCount = todos.filter((x) => x.completedDate != null).length;
        return (
          <>
            {todos?.length > 0 && (
              <Badge pill={true} className={`d-flex greybadge`}>
                <div style={{ padding: "2px 6px", fontSize: "12px" }} className={`d-flex align-items-center `}>
                  <CheckIcon completedNr={completedCount} totalNr={todos.length} />
                  {completedCount} / {todos.length}
                </div>
              </Badge>
            )}
          </>
        );
      },
    },
    {
      headerName: "",
      colId: "deleteColumn",
      width: 55,
      maxWidth: 55,
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => (
        <ConfirmDialog
          title="Slette agendapunkt?"
          message="Er du sikker på at du vil slette agendapunkt?"
          confirmCb={() =>
            updateAgendaItem({
              projectId: projectId,
              agendaId: params.data._id,
              attribute: "isDeleted",
              value: true,
            })
          }
        >
          <div style={{ scale: ".7" }}>
            <IconToggle
              toggled
              activeIcon="trash"
              inactiveIcon="trash"
              activeColor={STANDARD_COLORS.red}
              onClick={(e) => e.preventDefault()}
            />
          </div>
        </ConfirmDialog>
      ),
    },
  ];

  const { onGridReady, onStateUpdated, mergedColumns } = useAgGridReduxState<IMeeting.AgendaItem>(
    "agenda",
    baseColumns,
  );

  const onRowClicked = (params) => {
    const target = params.event.target as HTMLElement;
    const isExpandTrigger = target.closest("[data-expand-trigger='true']");

    if (!isExpandTrigger) {
      return;
    }

    params.api.forEachNode((node) => {
      if (node.expanded && node !== params.node) {
        node.setExpanded(false);
      }
    });
    params.node.setExpanded(!params.node.expanded);
    params.api.refreshCells();
    params.api.redrawRows();
  };
  const [updateAgendaItem] = useUpdateMeetingAgendaMutation();

  const expandRow = {
    renderer: (row) => (
      <AgendaItem
        agendaItem={row}
        onAgendaItemChange={(oldValue, newValue, param) =>
          updateAgendaItem({
            projectId: projectId,
            agendaId: row._id,
            attribute: param,
            value: newValue,
          })
        }
      />
    ),
    showExpandColumn: true,
    isAnyExpands: true,
    onlyOneExpanding: false,
    expandHeaderColumnRenderer: expandHeaderColumn,
    expandColumnRenderer: expandColumnCaret,
    expandByColumnOnly: false,
  };
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      // filter: true,
      suppressHeaderMenuButton: true,
      suppressMovable: false,
      resizable: true,
      wrapText: true,
      autoHeight: true,
      cellClassRules: {
        "not-relevant-cell": (params) => params.data.notRelevant === true,
      },
      cellClass: "py-0",
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    }),
    [],
  );
  const [createAgendaItem, { isError: addAgendaError }] = useCreateMeetingAgendaItemMutation();
  const [updateAllAgendaIds, { isError: updateAgendaIdError }] = useUpdateAllAgendaIdsMutation();
  const DetailCellRenderer = ({ data, node, api }: CustomCellRendererProps) => {
    return (
      <>
        <AgendaItem
          agendaItem={data}
          onAgendaItemChange={(oldValue, newValue, param) =>
            updateAgendaItem({
              projectId: projectId,
              agendaId: data._id,
              attribute: param,
              value: newValue,
            })
          }
        />
        <div style={{ height: "2px" }} />
      </>
    );
  };
  const rowData = useMemo(() => {
    return (agendaItems?.filter((item) => item.isDeleted !== true) || []).map((item) => ({ ...item }));
  }, [agendaItems]);
  const detailCellRenderer = useCallback(DetailCellRenderer, []);

  const sideBar = useMemo(() => {
    const sb: SideBarDef = {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
      ],
      position: "left",
    };
    return sb;
  }, []);

  return (
    <div className="d-flex flex-column w-100 p-2 h-100 overflow-auto">
      {addAgendaError === true && <FailedAlert />}
      {updateAgendaIdError === true && <FailedAlert />}
      {sendAgendaMailModalOpen === true && (
        <SendMeetingAgendaByMailModal meeting={singleMeeting} close={() => setSendAgendaMailModalOpen(false)} />
      )}
      <Group gap="xs" align="center" wrap="nowrap" w="100%" mb="xs">
        <CreateNewButton
          onClick={() =>
            createAgendaItem({
              projectId: projectId,
              meetingId: singleMeeting._id,
            })
          }
          size="sm"
          tooltip="Legg til agendapunkt"
        />

        <Tooltip label="Oppdater ID" withArrow offset={4}>
          <Button
            size="sm"
            color="mc-primary"
            variant="outline"
            onClick={() =>
              updateAllAgendaIds({
                projectId: projectId,
                meetingId: singleMeeting._id,
              })
            }
            leftSection={<IconSortAscendingNumbers size={16} />}
          >
            ID
          </Button>
        </Tooltip>
        <TextInput
          size="sm"
          placeholder="Søk…"
          value={filterText}
          onChange={(e) => setFilterText(e.currentTarget.value)}
          w={170}
          rightSection={filterText.length > 0 && <CloseButton onClick={() => setFilterText("")} size="sm" />}
        />
        <Button
          size="sm"
          variant={userFilterActive ? "outline" : "outline"}
          color={userFilterActive ? "mc-primary" : "warmgray.6"}
          bg={userFilterActive ? "mc-primary.1" : undefined}
          onClick={() => setUserFilterActive(!userFilterActive)}
          leftSection={userFilterActive ? <IconUserCheck size={16} /> : undefined}
        >
          Mine agendapunkter
        </Button>
        <Box className="flex-fill" />
        <ReactToPrint
          onBeforeGetContent={() => {
            setPrinterFriendly(gridRef.current!.api);
          }}
          onAfterPrint={() => setNormal(gridRef.current!.api)}
          trigger={() => (
            <Tooltip label="Tabellutskrift" withArrow offset={2}>
              <Button color="mc-primary" px="sm" variant="outline" size="sm">
                <IconPrinter size={16} />
              </Button>
            </Tooltip>
          )}
          content={() => refContainer.current}
        />
        <Tooltip label="Send agenda per mail" withArrow offset={2}>
          <Button
            size="sm"
            color="mc-primary"
            variant="outline"
            onClick={() => setSendAgendaMailModalOpen(true)}
            leftSection={<IconSend size={16} />}
          >
            Send agenda
          </Button>
        </Tooltip>
      </Group>
      <div className="meetingTable ag-theme-quartz" style={{ height: "100%" }} id="myGrid" ref={refContainer}>
        <AgGridReact
          rowHeight={56}
          ref={gridRef}
          rowData={rowData}
          getRowId={getRowId}
          columnDefs={mergedColumns}
          sideBar={sideBar}
          defaultColDef={defaultColDef}
          rowSelection={{ mode: "singleRow", checkboxes: false }}
          animateRows
          suppressContextMenu
          suppressMoveWhenRowDragging
          rowDragManaged
          onRowDragEnd={onRowDragEnd}
          onGridReady={onGridReady}
          onColumnMoved={(e) => {
            if (e.finished) onStateUpdated();
          }}
          onColumnVisible={onStateUpdated}
          onColumnResized={(e) => {
            if (e.finished) onStateUpdated();
          }}
          onColumnPinned={onStateUpdated}
          quickFilterText={filterText}
          includeHiddenColumnsInQuickFilter
          masterDetail
          detailRowAutoHeight
          detailCellRenderer={detailCellRenderer}
          onRowClicked={onRowClicked}
          localeText={AG_GRID_LOCALE_NOR}
          doesExternalFilterPass={doesExternalFilterPass}
          isExternalFilterPresent={() => userFilterActive}
          stopEditingWhenCellsLoseFocus={true}
          readOnlyEdit={true}
          onCellEditRequest={(event) => {
            updateAgendaItem({
              projectId: projectId,
              agendaId: event.data._id,
              attribute: (event.colDef.context as { updateAttribute?: string } | undefined)?.updateAttribute ?? event.colDef.field,
              value: event.newValue,
            });
          }}
        />
      </div>
    </div>
  );
};

export default Agenda;
